import React, { useEffect, useState, useMemo } from "react";
import {
  Button,
  Modal,
  notification,
  Form,
  Select,
  message,
} from "lib/ui";
import { PlusOutlined, DeleteOutlined } from "lib/icons";
import {
  CreditCard,
  CheckCircle,
  XCircle,
  Layers,
  Pencil,
  Trash2,
} from "lucide-react";
import AddForm from "./Add";
import ApiPath from "../../../hooks/ApiFetch";
import {
  AdminPageLayout,
  AdminHero,
  AdminStats,
  AdminSearchBar,
  AdminFilterTabs,
} from "../../../components/admin/AdminPageShell";
import {
  PeopleDataTable,
  ActionBtn,
  BoolPill,
} from "../../../components/admin/PeopleDataTable";

const { Option } = Select;

const ENABLED_FILTERS = [
  { id: "all", label: "All" },
  { id: "yes", label: "Enabled" },
  { id: "no", label: "Disabled" },
];

const PaymentMethodsPage = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [form] = Form.useForm();
  const [search, setSearch] = useState("");
  const [enabledFilter, setEnabledFilter] = useState("all");
  const [data, setData] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await ApiPath.get("/paymentmethods");
      setData(Array.isArray(response.data) ? response.data : []);
    } catch {
      message.error("Failed to load payment methods");
    }
  };

  const handleAdd = () => {
    setEditingPayment(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (row) => {
    setEditingPayment(row);
    form.setFieldsValue(row);
    setIsModalVisible(true);
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: "Delete payment method?",
      icon: <DeleteOutlined />,
      okText: "Delete",
      cancelText: "Cancel",
      okType: "danger",
      onOk: async () => {
        try {
          await ApiPath.delete(`/paymentmethods/${id}`);
          setData((prev) => prev.filter((item) => item._id !== id));
          notification.success({
            message: "Deleted",
            description: "Payment method removed.",
          });
        } catch {
          notification.error({
            message: "Failed",
            description: "Could not delete.",
          });
        }
      },
    });
  };

  const handleSave = async (values) => {
    try {
      if (editingPayment) {
        await ApiPath.put(`/paymentmethods/${editingPayment._id}`, values);
        setData((prev) =>
          prev.map((item) =>
            item._id === editingPayment._id ? { ...item, ...values } : item
          )
        );
        notification.success({
          message: "Updated",
          description: "Payment method saved.",
        });
      } else {
        const response = await ApiPath.post("/paymentmethods", values);
        setData((prev) => [
          ...prev,
          { ...values, _id: response.data._id ?? response.data?._id },
        ]);
        notification.success({
          message: "Added",
          description: "Payment method created.",
        });
      }
      setIsModalVisible(false);
      form.resetFields();
    } catch {
      notification.error({
        message: "Failed",
        description: "Could not save payment method.",
      });
    }
  };

  const filteredData = useMemo(() => {
    const q = search.trim().toLowerCase();
    return data.filter((item) => {
      if (enabledFilter === "yes" && !item.enabled) return false;
      if (enabledFilter === "no" && item.enabled) return false;
      if (!q) return true;
      return [item.title, item.slug, item.meta, item.type]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q);
    });
  }, [data, search, enabledFilter]);

  const enabledCount = data.filter((d) => d.enabled).length;

  const columns = [
    {
      key: "title",
      header: "Title",
      render: (row) => (
        <span className="font-semibold text-slate-900">{row.title || "—"}</span>
      ),
    },
    {
      key: "slug",
      header: "Slug",
      render: (row) => (
        <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-700">
          {row.slug || "—"}
        </code>
      ),
    },
    {
      key: "type",
      header: "Type",
      render: (row) => (
        <span className="text-sm capitalize text-slate-600">{row.type || "—"}</span>
      ),
    },
    {
      key: "meta",
      header: "Meta",
      render: (row) => (
        <span className="line-clamp-2 max-w-[200px] text-xs text-slate-500">
          {row.meta || "—"}
        </span>
      ),
    },
    {
      key: "enabled",
      header: "Status",
      render: (row) => (
        <BoolPill
          value={row.enabled}
          trueLabel="Enabled"
          falseLabel="Disabled"
        />
      ),
    },
  ];

  return (
    <AdminPageLayout>
      <AdminHero
        icon={CreditCard}
        badge="Checkout"
        title="Payment methods"
        description="Gateways and options customers see when paying for orders."
        actions={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
            className="!border-0 !bg-white !text-brand-dark hover:!bg-violet-50"
          >
            Add method
          </Button>
        }
      />

      <AdminStats
        items={[
          { label: "Total methods", value: data.length, icon: CreditCard },
          { label: "Enabled", value: enabledCount, icon: CheckCircle },
          { label: "Disabled", value: data.length - enabledCount, icon: XCircle },
          { label: "Showing", value: filteredData.length, icon: Layers },
        ]}
      />

      <AdminSearchBar
        placeholder="Search title, slug, type, meta…"
        value={search}
        onChange={setSearch}
        extra={
          <AdminFilterTabs
            tabs={ENABLED_FILTERS}
            active={enabledFilter}
            onChange={setEnabledFilter}
          />
        }
      />

      <PeopleDataTable
        columns={columns}
        rows={filteredData}
        rowKey="_id"
        renderActions={(row) => (
          <>
            <ActionBtn variant="edit" onClick={() => handleEdit(row)}>
              <Pencil size={14} />
              Edit
            </ActionBtn>
            <ActionBtn variant="delete" onClick={() => handleDelete(row._id)}>
              <Trash2 size={14} />
              Delete
            </ActionBtn>
          </>
        )}
        emptyTitle="No payment methods"
        emptyHint="Add a method or adjust filters."
      />

      <Modal
        title={editingPayment ? "Edit payment method" : "Add payment method"}
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={800}
      >
        <AddForm
          form={form}
          onFinish={handleSave}
          initialValues={editingPayment || {}}
        />
      </Modal>
    </AdminPageLayout>
  );
};

export default PaymentMethodsPage;
